import React, { useEffect, useState } from 'react';
import { Button, Input, Spin, Alert } from 'antd';

import { BASE_URL } from '../../utils/constants'
import Layout from '../../components/Layout';
import ImageUploader from '../../components/ImageUploader';
import api from '../../api/api'

const { TextArea } = Input;

interface UserProfile {
  username: string;
  bio: string;
  image: string;
}

const Appearance: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile>({
    username: '',
    bio: '',
    image: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Replace with your actual API endpoint
        const response = await api.get('/v1/profile/');
        setProfile(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load profile', err);
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleImageChange = (file: File | null) => {
    setImageFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    
    try {
      const formData = new FormData();
      formData.append('username', profile.username);
      formData.append('bio', profile.bio);
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      // Replace with your actual API endpoint
      await api.post('/v1/profile/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      console.error('Failed to update profile', err);
      setMessage({ 
        type: 'error', 
        text: (err as any).response?.data?.message || 'Failed to update profile. Please try again.' 
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Appearance</h1>
          
          {loading ? (
            <div className="flex justify-center">
              <Spin size="large" />
            </div>
          ) : (
            <form onSubmit={handleSaveProfile}>
              <div className="space-y-8 divide-y divide-gray-200">
                {message && (
                  <Alert
                    message={message.text}
                    type={message.type}
                    showIcon
                    className="mb-4"
                  />
                )}

                <div className="pt-8">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Profile Information</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Update your profile information shown on your link page.
                    </p>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-6">
                      <ImageUploader
                        initialImage={profile.image}
                        onImageChange={handleImageChange}
                      />
                    </div>

                    <div className="sm:col-span-4">
                      <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                        Username
                      </label>
                      <Input
                        disabled
                        id="username"
                        name="username"
                        value={profile.username}
                        onChange={handleInputChange}
                        // prefix="@"
                      />
                    </div>

                    <div className="sm:col-span-4">
                      <label htmlFor="link_prefix" className="block text-sm font-medium text-gray-700 mb-2">
                        Link Prefix
                      </label>
                      <Input
                        disabled
                        id="link_prefix"
                        name="link_prefix"
                        value={profile.username}
                        onChange={handleInputChange}
                        prefix={BASE_URL + '/'}
                      />
                    </div>

                    <div className="sm:col-span-6">
                      <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                        Bio
                      </label>
                      <TextArea
                        id="bio"
                        name="bio"
                        rows={3}
                        value={profile.bio}
                        onChange={handleInputChange}
                        placeholder="Brief description for your profile. URLs are hyperlinked."
                      />
                      <p className="mt-2 text-sm text-gray-500">
                        Brief description for your profile. URLs are hyperlinked.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-5">
                  <div className="flex justify-end space-x-3">
                    <Button>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={saving}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Appearance; 